/* global BigInt */
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { Table, Spinner, Container, Button } from "react-bootstrap";
import { useAccount, useChainId, useWalletClient, useSwitchNetwork } from 'wagmi'
import { formatUnits, parseAbiItem } from 'viem'
import { extractWithdrawalMessageLogs, getGame, getWithdrawalStatus, getWithdrawals, walletActionsL1 } from 'viem/op-stack'
import ReactPaginate from 'react-paginate';
import Account from './Account';
import { createL1PublicClient, createL2PublicClient } from '../../utils/viemClient';

// WithdrawalInitiated 事件签名（用于识别提现的 token 与金额）
const WITHDRAWAL_INITIATED_EVENT = parseAbiItem('event WithdrawalInitiated(address indexed l1Token, address indexed l2Token, address indexed from, address to, uint256 amount, bytes extraData)');

const WithdrawAccount = () => {
    const { address, isConnected } = useAccount()
    const chainId = useChainId()
    const { switchNetwork } = useSwitchNetwork()
    const { data: walletClientRaw } = useWalletClient()
    const walletClient = useMemo(() => walletClientRaw ? walletClientRaw.extend(walletActionsL1()) : null, [walletClientRaw])

    const [transactionLoader, setTransactionLoader] = useState(false)
    const [actionLoadingIndex, setActionLoadingIndex] = useState(null)
    const [withdrawDetails, setWithdrawDetails] = useState([])
    const isLoadingRef = useRef(false)

    const l1Client = useMemo(() => {
        try {
            return createL1PublicClient()
        } catch (error) {
            console.error('初始化 L1 客户端失败:', error)
            return null
        }
    }, [])

    const l2Client = useMemo(() => {
        try {
            return createL2PublicClient()
        } catch (error) {
            console.error('初始化 L2 客户端失败:', error)
            return null
        }
    }, [])

    const l1ChainId = Number(process.env.REACT_APP_L1_CHAIN_ID) || 11155111
    const portalAddress = process.env.REACT_APP_OPTIMISM_PORTAL_PROXY
    const disputeGameFactoryAddress = process.env.REACT_APP_DISPUTE_GAME_FACTORY_PROXY
    const l2BridgeAddress = process.env.REACT_APP_L2_BRIDGE || '0x4200000000000000000000000000000000000010'
    const l2ExplorerUrl = (process.env.REACT_APP_L2_EXPLORER_URL || 'https://scan.dev.goalpha.org').replace(/\/$/, '')

    const tokenList = useMemo(() => ([
        { type: process.env.REACT_APP_L2_DAI, tokenSymbol: "DAI", decimalValue: 18 },
        { type: process.env.REACT_APP_L2_USDT, tokenSymbol: "USDT", decimalValue: 6 },
        { type: process.env.REACT_APP_L2_USDC, tokenSymbol: "USDC", decimalValue: 6 },
        { type: process.env.REACT_APP_L2_wBTC, tokenSymbol: "wBTC", decimalValue: 8 }
    ]), [])

    const formatTokenAmount = useCallback((amount, l2Token) => {
        if (amount === undefined || amount === null) return '0'
        const tokenMeta = tokenList.find(token => token.type && l2Token && token.type.toLowerCase() === l2Token.toLowerCase())
        const decimals = tokenMeta?.decimalValue ?? 18
        const value = typeof amount === 'bigint' ? amount : BigInt(amount?._hex ?? amount ?? 0)
        return formatUnits(value, decimals)
    }, [tokenList])

    const getTokenSymbol = useCallback((l2Token) => {
        const tokenMeta = tokenList.find(token => token.type && l2Token && token.type.toLowerCase() === l2Token.toLowerCase())
        return tokenMeta?.tokenSymbol || 'ETH'
    }, [tokenList])

    const getStatusText = (viemStatus) => {
        switch (viemStatus) {
            case 'waiting-to-prove':
                return 'Waiting for Confirmation'
            case 'ready-to-prove':
                return 'Ready to Prove'
            case 'proving':
                return 'Proving...'
            case 'waiting-to-finalize':
                return 'In challenge Period'
            case 'ready-to-finalize':
                return 'Claim Withdrawal'
            case 'finalized':
                return 'Completed'
            default:
                return 'Unknown'
        }
    }

    const fetchStatus = useCallback(async (receipt, relativeIndex) => {
        if (!l1Client || !portalAddress || !disputeGameFactoryAddress) {
            return 'waiting-to-prove'
        }
        try {
            return await getWithdrawalStatus(l1Client, {
                receipt,
                logIndex: relativeIndex,
                portalAddress,
                disputeGameFactoryAddress,
                gameLimit: 200
            })
        } catch (error) {
            console.warn('获取提现状态失败:', error)
            return 'waiting-to-prove'
        }
    }, [l1Client, portalAddress, disputeGameFactoryAddress])

    const getWithdraw = useCallback(async () => {
        if (!address || !l1Client || !l2Client) {
            return
        }
        if (isLoadingRef.current) {
            return
        }
        isLoadingRef.current = true
        setTransactionLoader(false)

        try {
            const withdrawalLogs = await l2Client.getLogs({
                address: l2BridgeAddress,
                event: WITHDRAWAL_INITIATED_EVENT,
                args: { from: address },
                fromBlock: 0n,
                toBlock: 'latest'
            })

            const usedWithdrawalHashes = new Set()
            const formattedData = []

            for (const log of withdrawalLogs) {
                try {
                    const [receipt, block] = await Promise.all([
                        l2Client.getTransactionReceipt({ hash: log.transactionHash }),
                        l2Client.getBlock({ blockNumber: log.blockNumber })
                    ])

                    const messageLogs = extractWithdrawalMessageLogs({ logs: receipt.logs })
                    const withdrawalMessages = getWithdrawals({ logs: receipt.logs })

                    const targetMessageLog = messageLogs.find((msg) => {
                        const hash = msg.args.withdrawalHash?.toLowerCase()
                        if (!hash || usedWithdrawalHashes.has(hash)) {
                            return false
                        }
                        return true
                    })

                    if (!targetMessageLog) {
                        continue
                    }

                    const withdrawalHash = targetMessageLog.args.withdrawalHash
                    usedWithdrawalHashes.add(withdrawalHash.toLowerCase())

                    const withdrawalMessage = withdrawalMessages.find((entry) => entry.withdrawalHash === withdrawalHash)
                    if (!withdrawalMessage) {
                        console.warn('未匹配到 withdrawal 数据, 跳过此交易', {
                            transactionHash: log.transactionHash,
                            withdrawalHash
                        })
                        continue
                    }

                    const relativeIndex = messageLogs.findIndex((msg) => msg.args.withdrawalHash === withdrawalHash)
                    const messageStatus = await fetchStatus(receipt, relativeIndex >= 0 ? relativeIndex : 0)

                    formattedData.push({
                        transactionHash: log.transactionHash,
                        amount: log.args.amount,
                        l2Token: log.args.l2Token,
                        timestamp: Number(block.timestamp),
                        messageStatus,
                        withdrawal: withdrawalMessage,
                        logIndex: relativeIndex >= 0 ? relativeIndex : 0,
                        receipt,
                        l2BlockNumber: log.blockNumber
                    })
                } catch (itemError) {
                    console.error('处理单条提现记录失败:', itemError)
                }
            }

            formattedData.sort((a, b) => b.timestamp - a.timestamp)
            setWithdrawDetails(formattedData)
        } catch (error) {
            console.error('获取提取历史失败:', error)
            setWithdrawDetails([])
        } finally {
            setTransactionLoader(true)
            isLoadingRef.current = false
        }
    }, [address, fetchStatus, l1Client, l2Client, l2BridgeAddress])

    const handleProve = async (event, item, index) => {
        if (!walletClient || !l1Client || !l2Client) {
            alert('钱包或客户端未就绪，请检查连接。')
            return
        }
        if (!portalAddress || !disputeGameFactoryAddress) {
            alert('缺少 Optimism Portal 或 DisputeGameFactory 配置，请在环境变量中补全。')
            return
        }
        if (!address) {
            return
        }
        if (chainId !== l1ChainId && switchNetwork) {
            try {
                await switchNetwork(l1ChainId)
                alert('网络已切换至 L1，请再次点击 “Prove”。')
                return
            } catch (switchError) {
                alert('无法自动切换网络，请手动切到 L1 再试。')
                return
            }
        }

        setActionLoadingIndex(index)
        try {
            const game = await getGame(l1Client, {
                l2BlockNumber: BigInt(item.l2BlockNumber),
                portalAddress,
                disputeGameFactoryAddress
            })

            const proveArgs = await l2Client.buildProveWithdrawal({
                withdrawal: item.withdrawal,
                game
            })

            const hash = await walletClient.proveWithdrawal({
                ...proveArgs,
                account: walletClient.account ?? address,
                portalAddress
            })

            await l1Client.waitForTransactionReceipt({ hash })
            await getWithdraw()
            alert('Prove 成功，进入挑战期。')
        } catch (error) {
            console.error('Prove failed:', error)
            if (error?.code === 'ACTION_REJECTED') {
                return
            }
            alert(`Prove failed: ${error?.shortMessage || error?.message || '未知错误'}`)
        } finally {
            setActionLoadingIndex(null)
        }
    }

    const handleClaim = async (event, item, index) => {
        if (!walletClient || !l1Client) {
            alert('钱包或客户端未就绪，请检查连接。')
            return
        }
        if (!portalAddress) {
            alert('缺少 Optimism Portal 配置，请在环境变量中补全。')
            return
        }
        if (!address) {
            return
        }
        if (chainId !== l1ChainId && switchNetwork) {
            try {
                await switchNetwork(l1ChainId)
                alert('网络已切换至 L1，请再次点击 “Claim”。')
                return
            } catch (switchError) {
                alert('无法自动切换网络，请手动切到 L1 再试。')
                return
            }
        }

        setActionLoadingIndex(index)
        try {
            const hash = await walletClient.finalizeWithdrawal({
                withdrawal: item.withdrawal,
                account: walletClient.account ?? address,
                portalAddress
            })
            await l1Client.waitForTransactionReceipt({ hash })
            await getWithdraw()
            alert('Claim 成功，提款已完成。')
        } catch (error) {
            console.error('Claim failed:', error)
            if (error?.code === 'ACTION_REJECTED') {
                return
            }
            alert(`Claim failed: ${error?.shortMessage || error?.message || '未知错误'}`)
        } finally {
            setActionLoadingIndex(null)
        }
    }

    useEffect(() => {
        if (isConnected && address && l1Client && l2Client) {
            getWithdraw()
        } else if (!isConnected) {
            setWithdrawDetails([])
        }
    }, [address, isConnected, l1Client, l2Client, getWithdraw])

    // =============all Collections pagination start===============
    const [currentItemsCollections, setCurrentItemsCollections] = useState([])
    const [pageCountCollections, setPageCountCollections] = useState(0)
    const [itemOffsetCollections, setItemOffsetCollections] = useState(0)
    const itemsPerPageCollections = 10

    useEffect(() => {
        if (withdrawDetails) {
            const endOffsetCollections = itemOffsetCollections + itemsPerPageCollections
            setCurrentItemsCollections(withdrawDetails.slice(itemOffsetCollections, endOffsetCollections))
            setPageCountCollections(Math.ceil(withdrawDetails.length / itemsPerPageCollections))
        }
    }, [withdrawDetails, itemOffsetCollections])

    const handlePageClickCollections = (event) => {
        const newOffsetCollections =
            (event.selected * itemsPerPageCollections) % Math.max(withdrawDetails.length, 1)
        setItemOffsetCollections(newOffsetCollections)
    }
    // =============all Collections pagination end===============

    const timeConverter = (timestamp) => {
        const dateObj = new Date(timestamp * 1000)
        return dateObj.toLocaleString()
    }

    return (
        <>
            <div className="account_wrap">
                <Container>
                    <div className='account_inner_wrap'>
                        <Account />
                        <section className="account_withdraw_table">
                            {
                                !transactionLoader ? <div className="lds-ellipsis"><div></div><div></div><div></div><div></div></div> :
                                    withdrawDetails?.length <= 0 ? <h4 className='text-center text-white'>No Transaction Found</h4> :
                                        <Table responsive bordered hover variant="dark">
                                            <thead>
                                                <tr>
                                                    <th>Time</th>
                                                    <th>Type</th>
                                                    <th>Amount</th>
                                                    <th>Transaction</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentItemsCollections.map((element, index) => {
                                                    const globalIndex = itemOffsetCollections + index
                                                    const { timestamp, transactionHash, amount, messageStatus, l2Token } = element
                                                    const displayText = getStatusText(messageStatus)

                                                    return (
                                                        <tr key={transactionHash}>
                                                            <td>{timeConverter(timestamp)}</td>
                                                            <td>Withdraw</td>
                                                            <td>{formatTokenAmount(amount, l2Token)} {getTokenSymbol(l2Token)}</td>
                                                            <td>
                                                                <a href={`${l2ExplorerUrl}/tx/${transactionHash}`} target='_blank' rel="noreferrer">
                                                                    {`${transactionHash.slice(0, 8)}...${transactionHash.slice(-8)}`}
                                                                </a>
                                                            </td>
                                                            <td>
                                                                {displayText}
                                                                {messageStatus === 'ready-to-prove' && (
                                                                    <Button
                                                                        className='btn withdraw_inner_btn ms-2'
                                                                        disabled={actionLoadingIndex === globalIndex}
                                                                        onClick={(e) => handleProve(e, element, globalIndex)}
                                                                    >
                                                                        {actionLoadingIndex === globalIndex ? <Spinner size="sm" animation="border" /> : "Prove"}
                                                                    </Button>
                                                                )}
                                                                {messageStatus === 'ready-to-finalize' && (
                                                                    <Button
                                                                        className='btn withdraw_inner_btn ms-2'
                                                                        disabled={actionLoadingIndex === globalIndex}
                                                                        onClick={(e) => handleClaim(e, element, globalIndex)}
                                                                    >
                                                                        {actionLoadingIndex === globalIndex ? <Spinner size="sm" animation="border" /> : "Claim"}
                                                                    </Button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    )
                                                })}

                                            </tbody>
                                        </Table>}
                            {withdrawDetails?.length > itemsPerPageCollections ? <div className='pagination_wrap'>
                                <ReactPaginate
                                    breakLabel="..."
                                    nextLabel=" >>"
                                    onPageChange={handlePageClickCollections}
                                    pageRangeDisplayed={1}
                                    marginPagesDisplayed={1}
                                    pageCount={pageCountCollections}
                                    previousLabel="<< "
                                    containerClassName="pagination justify-content-end"
                                    pageClassName="page-item"
                                    pageLinkClassName="page-link"
                                    previousClassName="page-item"
                                    previousLinkClassName="page-link"
                                    nextClassName="page-item"
                                    nextLinkClassName="page-link"
                                    breakClassName="page-item"
                                    breakLinkClassName="page-link"
                                    activeClassName="active"
                                />
                            </div> : ""}
                        </section>
                    </div>
                </Container>
            </div>
        </>
    )
}

export default WithdrawAccount
